/* istanbul ignore file */
import { styled } from '@mui/material/styles';

import { px, rem, Theme } from '../../theme';
import { HorizontalRule } from '../HorizontalRule';

/**
 * Represents the levels of horizontal padding to align the footer items in a grid-like fashion
 * Mobile has two different levels - outer (e.g. the accordion container) and inner (e.g. the headings inside the accordion)
 * Desktop just has one level
 */
export const spacingPx = {
    outer: {
        mobile: 30,
        desktop: 80,
    },
    inner: {
        mobile: 20,
    },
};
export const StyledFooterWrapper = styled('footer')`
    ${({ theme }: { theme: Theme }) => `
        width: 100%;
        padding-top: ${theme.tggSpacing(px(32), 6)};
        background-color:${theme.getRebrandingColor(theme.palette.grey[100], theme.palette.tgg.surface.container.main)};
        
        ${theme.breakpoints.up('desktop')} {
        padding-top: ${theme.tggSpacing(0, 8)};
        }
        ${theme.breakpoints.up('largeDesktop')} {
        padding-top: ${theme.tggSpacing(0, 16)};
        }
	`}
`;
//todo replace "h2" with Heading as it was before when typography fixed
export const StyledFooterHeading = styled('h2')`
    ${({ theme }: { theme: Theme }) =>
        `
    margin: ${theme.tggSpacing('revert', 0)};
        padding-bottom: ${theme.tggSpacing(rem(1.5), 4)};
        text-transform:${theme.isRebranding('lowercase', 'unset')};
        ${theme.breakpoints.up('desktop')} {
        padding-top: ${theme.tggSpacing(px(15), 4)};
        padding-bottom: ${theme.tggSpacing(0, 0)};
        }
    `}
    ${({ theme }: { theme: Theme }) =>
        theme.isRebranding(
            theme.typography.heading_14_desktop_16_lh_16,
            theme.typography.h4Rebrand,
        )};
`;

export const StyledTopSection = styled('div')`
    ${({ theme }: { theme: Theme }) => `
            padding-top: ${theme.tggSpacing(px(spacingPx.inner.mobile), 0)};
        ${theme.breakpoints.up('desktop')} {
            display: flex;
            justify-content: space-between;
            padding-right: ${theme.tggSpacing(px(spacingPx.outer.desktop), 12)};
            padding-left: ${theme.tggSpacing(px(spacingPx.outer.desktop), 12)};
        }
        ${theme.breakpoints.up('largeDesktop')} {
            padding-right: ${theme.tggSpacing(px(spacingPx.outer.desktop), spacingPx.outer.desktop / 4)};
            padding-left: ${theme.tggSpacing(px(spacingPx.outer.desktop), spacingPx.outer.desktop / 4)};
        }
	`}
`;

export const StyledSocialIconsSection = styled('section')`
    ${({ theme }: { theme: Theme }) => `
        padding-right: ${theme.tggSpacing(px(spacingPx.outer.mobile + spacingPx.inner.mobile), 6)};
        padding-left: ${theme.tggSpacing(px(spacingPx.outer.mobile + spacingPx.inner.mobile), 6)};
        ${theme.breakpoints.up('desktop')} {
            padding-left: ${theme.tggSpacing(0, 0)};
        }
    `}
`;

export const StyledSocialIconsList = styled('ul')`
    ${({ theme }) => `
        display: flex;
        gap: ${theme.tggSpacing(rem(2), 4)};
        width: 100%;
        padding-left: ${theme.tggSpacing(0, 0)};
        margin: ${theme.tggSpacing(0, 0)};
        list-style: none;
        ${theme.breakpoints.up('desktop')} {
            padding-top: ${theme.tggSpacing(px(15), 4)};
        }
        li div {
                width: ${theme.tggSpacing(rem(4.2), rem(3))};
                height: ${theme.tggSpacing(rem(4.2), rem(3))};
                border-radius: ${theme.tggSpacing('50%', '50%')};
                background:${theme.getRebrandingColor(theme.palette.primary.main, theme.palette.tgg.surface.brand)};
                padding: ${theme.tggSpacing('unset', rem(0.75))};
                display:${theme.isRebranding('inline-flex', 'flex')};
                align-items: center;
                justify-content: center;
                svg {
                    margin-right: ${theme.tggSpacing(0, -0.4)};
                }
        },
`}
`;

export const StyledHorizontalRuleContainer = styled('div')`
    ${({ theme }: { theme: Theme }) => `
            display: ${theme.isRebranding('none', 'block')};
            padding-top: ${theme.tggSpacing(rem(0), 8)};
            padding-right: ${theme.tggSpacing(rem(0), 6)};
            padding-left: ${theme.tggSpacing(rem(0), 6)};
            padding-bottom: ${theme.tggSpacing(rem(0), 6)};
        ${theme.breakpoints.up('desktop')} {
            display: block;
            padding-top: ${theme.tggSpacing(rem(3), 8)};
            padding-right: ${theme.tggSpacing(px(spacingPx.outer.desktop), 12)};
            padding-left: ${theme.tggSpacing(px(spacingPx.outer.desktop), 12)};
        }
        ${theme.breakpoints.up('largeDesktop')} {
            padding-right: ${theme.tggSpacing(px(spacingPx.outer.desktop), spacingPx.outer.desktop / 4)};
            padding-left: ${theme.tggSpacing(px(spacingPx.outer.desktop), spacingPx.outer.desktop / 4)};
        }
        hr {
               background-color: ${theme.getRebrandingColor(theme.palette.common.blue, theme.palette.tgg.surface.action.selection.pressed)};
            },
        `}
`;

export const StyledHorizontalRule = styled(HorizontalRule)`
    ${({ theme }: { theme: Theme }) => `    
    margin-left: ${theme.tggSpacing(0, 0)};
    margin-right: ${theme.tggSpacing(0, 0)};
    background-color:${theme.getRebrandingColor(theme.palette.common.blue, theme.palette.tgg.surface.action.selection.pressed)};
	`}
`;

export const StyledSiteLinksSection = styled('div')`
    ${({ theme }: { theme: Theme }) => `
        padding-top: ${theme.tggSpacing(px(spacingPx.inner.mobile), 0)};
        padding-right: ${theme.tggSpacing(px(spacingPx.outer.mobile), 6)};
        padding-left: ${theme.tggSpacing(px(spacingPx.outer.mobile), 6)};
        ${theme.breakpoints.up('desktop')} {
            display: flex;
            justify-content: space-between;
            padding-right: ${theme.tggSpacing(px(spacingPx.outer.desktop), 12)};
            padding-left: ${theme.tggSpacing(px(spacingPx.outer.desktop), 12)};
            padding-bottom: ${theme.tggSpacing(px(spacingPx.outer.mobile), 12)};
        }
        ${theme.breakpoints.up('largeDesktop')} {
            padding-right: ${theme.tggSpacing(px(spacingPx.outer.desktop), 20)};
            padding-left: ${theme.tggSpacing(px(spacingPx.outer.desktop), 20)};
        }
    details {
                padding: ${theme.tggSpacing([rem(2.5), rem(2), rem(2)], [6, 0])};
              border-bottom: ${theme.tggSpacing(rem(0.06), rem(0.0625))} solid ${theme.isRebranding(theme.palette.common.blue, theme.palette.tgg.surface.action.selection.pressed)};
            },
    details:not([open]) {
            summary div {
               position: relative;
               }
              svg {
                position: absolute;
                top: ${theme.tggSpacing(0, 2)};
                right: 0;
    },
            },
    li {
               padding-top: ${theme.tggSpacing(rem(1.5), 4)};},
    a {
               padding-top: ${theme.tggSpacing(rem(1.5), 0)};
               text-transform:${theme.tggSpacing('lowercase', 'unset')} ;
               color: ${theme.getRebrandingColor('', theme.palette.tgg.text.label)};},
    svg {
                width: ${theme.tggSpacing('unset', '100%')};
                height: ${theme.tggSpacing('unset', '100%')};
                max-width: ${theme.tggSpacing('unset', 6)};
                max-height: ${theme.tggSpacing('unset', 6)};
                color: ${theme.getRebrandingColor('', theme.palette.tgg.text.label)};
    },
    svg g{
                fill: ${theme.getRebrandingColor(
                    theme.palette.primary.main,
                    theme.palette.tgg.surface.brand,
                )}
    },
`}
    summary p {
        ${({ theme }: { theme: Theme }) =>
            theme.isRebranding('', theme.typography.h4Rebrand)};
    }
    a {
        ${({ theme }: { theme: Theme }) =>
            theme.isRebranding('', theme.typography.buttonTextLinkRebrand)};
    }
    summary > div {
        ${({ theme }: { theme: Theme }) =>
            ` height: ${theme.tggSpacing('unset', 6)};`}
    }
`;

export const StyledSiteLinkColumn = styled('section')`
    ${({ theme }: { theme: Theme }) => `
        ${theme.breakpoints.up('desktop')} {
            width: 25%;
            margin-right: ${theme.tggSpacing(0, 6)};
        }
	`}
`;

export const StyledAppStoreBadgesSection = styled('section')`
    ${({ theme }: { theme: Theme }) => `
        padding: ${theme.tggSpacing(px(spacingPx.inner.mobile), [6, 0, 4])};
        margin-bottom: ${theme.tggSpacing(rem(1), 0)};
        ${theme.breakpoints.up('desktop')} {
        h2{
        margin-bottom: ${theme.tggSpacing(0, 4)};
        }
            padding: ${theme.tggSpacing(0, [rem(4.5), 0, 0, 0])};
        }
	`}
`;
export const StyledAwardBadge = styled('div')`
    display: flex;
    align-items: center;
    img {
        object-fit: contain;
    }
`;

export const StyledAppStoreBadges = styled('div')`
    ${({ theme }: { theme: Theme }) => `
        width: ${theme.tggSpacing(rem(27.5), 'unset')};
        > div{
        align-items: flex-start;
        justify-content: flex-start;
        > div{
         gap:${theme.tggSpacing(rem(1), 2.5)};
        ${theme.breakpoints.up('desktop')} {
        
         gap:${theme.tggSpacing(rem(1), 2.5)};
            flex-direction: column;
    align-items: flex-start;
        }
        ${theme.breakpoints.up('largeDesktop')} {
        flex-direction: unset;
        }
        }
        }
        img {
            height: ${theme.tggSpacing(rem(4), 10)};
        }
        ${theme.breakpoints.up('desktop')} {
            width: ${theme.tggSpacing(rem(24.5), 'unset')};
        }
	`}
`;
export const StyledAwardsContainer = styled('div')`
    ${({ theme }: { theme: Theme }) => `
        display: flex;
        gap: ${theme.tggSpacing(rem(2), 5)};
        justify-content: center;
        width: 100%;
        padding: ${theme.tggSpacing(rem(3), 7.5)};
        padding: ${theme.tggSpacing(px(spacingPx.outer.mobile), spacingPx.outer.mobile / 4)};
        background-color: ${theme.palette.primary.main};
        ${theme.breakpoints.up('desktop')} {
            gap: ${theme.tggSpacing(rem(8), 20)};
            padding: ${theme.tggSpacing([rem(3), rem(8)], [7.5, 20])};
        }
	`}
`;
